import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeading } from "../components/Headings/PageHeading";
import { SectionHeading } from "../components/Headings/SectionHeading";
import { DashboardPageLayout } from "../components/Layouts/DashboardPageLayout";
import { LoadingLayout } from "../components/Layouts/LoadingLayout";
import { OrderRow } from "../components/Rows/OrderRow";
import { getOrders } from "../api/orders";
import { useInfiniteQuery } from "react-query";
import { useForm } from "react-hook-form";
import { OrderFilters } from "../components/Filters/OrderFilters";
import { Link } from "react-router-dom";
import { PiPackage } from "react-icons/pi";
import { FiArrowRight } from "react-icons/fi";

function NoOrders() {
  return (
    <div className="flex flex-col items-center justify-center py-20 bg-white border border-gray-200 rounded-2xl space-y-6">
      <div className="flex items-center justify-center w-16 h-16 bg-slate-100 rounded-xl shadow-sm">
        <PiPackage className="w-8 h-8 text-blue" />
      </div>

      <h3 className="text-gray-800 font-semibold text-2xl">No Orders Yet</h3>

      <p className="text-gray-500 text-center max-w-xs">
        You haven’t created any shipments. Book your first one to get started!
      </p>

      <Link
        to="/dashboard/ship"
        className="mt-4 inline-flex items-center px-8 py-3 rounded-xl bg-white text-black font-medium text-lg border border-gray-300 shadow-sm hover:bg-gray-50 hover:text-blue transition-all duration-200"
      >
        Create Shipment
        <FiArrowRight className="ml-2 w-5 h-5" />
      </Link>
    </div>
  );
}

export function Orders() {
  const { control, watch, setValue } = useForm({
    defaultValues: {
      dateRange: [null, null],
      status: ["CANCELLED","CONFIRMED"],
    },
  });

  const status = watch("status");
  const dateRange = watch("dateRange");
  const [startDate, endDate] = dateRange || [];
  const [confirmedStartDate, setConfirmedStartDate] = useState(null);
  const [confirmedEndDate, setConfirmedEndDate] = useState(null);

  const [hasEverHadOrders, setHasEverHadOrders] = useState(null);

  const isDateRangeSelected =
    (!!confirmedStartDate && !!confirmedEndDate) ||
    (!confirmedStartDate && !confirmedEndDate);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      "orders",
      { status, startDate: confirmedStartDate, endDate: confirmedEndDate },
    ],
    ({ pageParam }) =>
      getOrders({
        pageParam,
        status,
        startDate: confirmedStartDate,
        endDate: confirmedEndDate,
      }),
    {
      getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey || undefined,
      enabled: !!status.length && isDateRangeSelected,
    }
  );

  // Sync date range filters
  useEffect(() => {
    const bothDatesSelected = !!startDate && !!endDate;
    const noDatesSelected = !startDate && !endDate;

    if (bothDatesSelected || noDatesSelected) {
      setConfirmedStartDate(startDate);
      setConfirmedEndDate(endDate);
    }
  }, [startDate, endDate]);

  // Flatten all orders across pages
  const allItems = data?.pages?.flatMap((p) => p.items) || [];

  // --- Sort globally by orderDate descending ---

  const hasAnyOrders = allItems.length > 0;

  // Track if the user ever had any orders
  useEffect(() => {
    if (!isLoading && data && hasEverHadOrders === null) {
      setHasEverHadOrders(allItems.length > 0);
    }
  }, [data, isLoading, hasEverHadOrders, allItems.length]);

  // IntersectionObserver for infinite scrolling
  const observerTarget = useRef(null);
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 0.1,
    });

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [handleObserver]);

  if (isError) return <div>Oops, something went wrong...</div>;
  if (isLoading) return <LoadingLayout />;

  const showNoOrdersPage = hasEverHadOrders === false;
  const showNoResultsForFilters = hasEverHadOrders && !hasAnyOrders;

  return (
    <DashboardPageLayout>
      <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-4 lg:py-4">
        <div className="mx-auto max-w-4xl space-y-10 lg:mx-0 lg:max-w-none">
          <PageHeading title="Orders" withBreadcrumbs={true} />

          <SectionHeading
            filters={
              hasEverHadOrders && (
                <OrderFilters control={control} setValue={setValue} />
              )
            }
          />

          {/* --- EMPTY STATES --- */}
          {showNoOrdersPage && <NoOrders />}
          {showNoResultsForFilters && (
            <div className="py-20 text-center text-gray-500 text-lg">
              No orders match the current filters.
            </div>
          )}

          {/* --- TABLE UI --- */}
          {hasAnyOrders && (
            <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-200">
              <div
                className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 
                text-gray-700 text-xs font-semibold uppercase tracking-wide border-b border-gray-200"
              >
                <div className="col-span-4">Shipment</div>
                <div className="col-span-3">Date</div>
                <div className="col-span-3 text-right">Amount</div>
                <div className="col-span-2 text-right"></div>
              </div>

              {allItems.map((o) => (
                <OrderRow key={o.orderId} order={o} />
              ))}
            </div>
          )}

          {/* --- Infinite scroll loading --- */}
          {hasAnyOrders && isFetchingNextPage && <LoadingLayout />}
          {hasAnyOrders && hasNextPage && <div ref={observerTarget} className="h-2" />}
        </div>
      </main>
    </DashboardPageLayout>
  );
}